import Video, { LocalParticipant, LocalVideoTrack, LocalVideoTrackPublication } from "twilio-video";
import {
  RecordingServiceOptions,
  RecordingService,
} from "../common/hooks/useRecorder";
import { Twilio } from "../globals/enums";
import { handleError } from "../helpers/sentry";
import { getTwilioVideoConstraintConfig } from "../config/constants";

export class TwilioService implements RecordingService {
  private options: RecordingServiceOptions = {};
  public videoTrack = null;
  public twilioRoom = null;
  private networkLevel;
  private statsInterval: NodeJS.Timeout;
  private previousStats: { [trackSid: string]: { bytesSent: number; timestamp: number } } = {};

  handleNetworkQualityChange = (connect) => {
    const localNetworkQuality = connect.localParticipant?.networkQualityLevel;

    if (localNetworkQuality !== undefined) {
      const isBelowThreshold = localNetworkQuality < Twilio.NetworkQualityLevelThreshold;
      const isAboveThreshold = localNetworkQuality >= Twilio.NetworkQualityLevelThreshold;
      const currentAboveThreshold = this.networkLevel >= Twilio.NetworkQualityLevelThreshold;

      if (
        (isBelowThreshold && currentAboveThreshold) ||
        (isAboveThreshold && !currentAboveThreshold)
      ) {
        this.networkLevel = localNetworkQuality;
        return isAboveThreshold;
      }
    }

    return false;
  };

  // Helper method to apply video constraints to a MediaStreamTrack
  private async applyVideoConstraints(
    track: MediaStreamTrack,
    constraintConfig: any
  ): Promise<MediaStreamTrack | null> {
    if (track.kind !== "video") return null;

    // Get current settings to check if we need to downscale
    const settings = track.getSettings();
    const currentWidth = settings.width || 640;
    const currentHeight = settings.height || 480;
    
    console.log("Current camera settings:", {
      width: currentWidth,
      height: currentHeight,
      frameRate: settings.frameRate
    });
    
    // Create a new MediaStream with the track to clone it
    const stream = new MediaStream([track]);
    const clonedTrack = stream.getVideoTracks()[0];
    
    // Always apply constraints regardless of current resolution
    // This ensures we override browser defaults
    try {
      const constraints = {
        width: { exact: constraintConfig.camera.width },
        height: { exact: constraintConfig.camera.height },
        frameRate: { exact: constraintConfig.camera.frameRate }
      };
      
      console.log("Applying strict camera constraints:", constraints);
      await clonedTrack.applyConstraints(constraints);
      
      // Log the actual settings after constraints are applied
      const newSettings = clonedTrack.getSettings();
      console.log("Actual camera settings after strict constraints:", {
        width: newSettings.width,
        height: newSettings.height,
        frameRate: newSettings.frameRate
      });
      
      return clonedTrack;
    } catch (error) {
      console.error("Error applying strict camera constraints:", error);
      
      // Try with less strict constraints
      try {
        const fallbackConstraints = {
          width: { ideal: constraintConfig.camera.width },
          height: { ideal: constraintConfig.camera.height },
          frameRate: { ideal: constraintConfig.camera.frameRate }
        };
        
        console.log("Applying fallback camera constraints:", fallbackConstraints);
        await clonedTrack.applyConstraints(fallbackConstraints);
        
        const fallbackSettings = clonedTrack.getSettings();
        console.log("Actual camera settings after fallback constraints:", {
          width: fallbackSettings.width,
          height: fallbackSettings.height,
          frameRate: fallbackSettings.frameRate
        });
        
        return clonedTrack;
      } catch (fallbackError) {
        console.error("Error applying fallback camera constraints:", fallbackError);
        
        // Try with minimum constraints - just frameRate
        try {
          const minimalConstraints = {
            frameRate: { max: constraintConfig.camera.frameRate }
          };
          
          console.log("Applying minimal camera constraints:", minimalConstraints);
          await clonedTrack.applyConstraints(minimalConstraints);
          return clonedTrack;
        } catch (minimalError) {
          console.error("Error applying minimal camera constraints:", minimalError);
          return null;
        }
      }
    }
  }

  async connect(options: RecordingServiceOptions) {
    this.options = options;
    try {
      const orgId = options.orgId || "";
      const constraintConfig = getTwilioVideoConstraintConfig(orgId);
      // Use config values if enabled, else fallback to defaults
      const maxVideoBitrate = constraintConfig?.maxVideoBitrate ?? 
        (process.env.REACT_APP_TWILIO_PUBLISH_BITRATE
          ? parseInt(process.env.REACT_APP_TWILIO_PUBLISH_BITRATE, 10)
          : 1000000);
      // First, get access to the camera with minimal constraints
      let constrainedVideoTrack: MediaStreamTrack | null = null;
      let localVideoTrack: LocalVideoTrack | null = null;
      
      if (constraintConfig) {
        try {
          // Get access to camera with minimal constraints first
          const stream = await navigator.mediaDevices.getUserMedia({ 
            video: true
          });
          
          const originalTrack = stream.getVideoTracks()[0];
          
          // Apply our specific constraints to create a new constrained track
          if (originalTrack) {
            constrainedVideoTrack = await this.applyVideoConstraints(originalTrack, constraintConfig);
            
            if (constrainedVideoTrack) {
              // Create a Twilio LocalVideoTrack from our constrained MediaStreamTrack
              localVideoTrack = new Video.LocalVideoTrack(constrainedVideoTrack, {
                name: 'secondary-camera'
              });
              
              console.log("Created Twilio LocalVideoTrack with constrained settings");
            }
          }
        } catch (error) {
          console.error("Failed to access camera or apply constraints:", error);
        }
      }
      
      // Connect options
      const connectOptions: any = {
        audio: false,
        bandwidthProfile: {
          video: {
            trackSwitchOffMode: "disabled"
          }
        },
        networkQuality: {
          local: Twilio.NetworkQualityLocal,
          remote: Twilio.NetworkQualityRemote
        },
        maxVideoBitrate,
        automaticSubscription: false
      };
      
      // If we successfully created a constrained LocalVideoTrack, use it
      if (localVideoTrack) {
        connectOptions.tracks = [localVideoTrack];
      } else {
        // Fallback to using constraints directly
        connectOptions.video = {
          aspectRatio: 16/9
        };
      }
      
      const room = await Video.connect(options.streamingToken, connectOptions);

      room.on("disconnected", (Room, error) => {
        console.log("Room disconnected", Room, error);
        if(error) {
          handleError(error);
        }
        clearInterval(this.statsInterval);
        this.options.onConnectionFailure();
      });

      room.on("reconnecting", (error) => {
        console.log("Room reconnecting", error);
        if(error) {
          handleError(error);
        }
      });

      room.on("reconnected", () => {
        console.log("Room reconnected");
      });

      room.localParticipant.on("networkQualityLevelChanged", () => {
        const isGoodQuality = this.handleNetworkQualityChange(room);
        options.onNetworkQualityChange(isGoodQuality);
      });
      
      // Log the track's dimensions and constraints when published
      room.localParticipant.on("trackPublished", (publication: LocalVideoTrackPublication) => {
        console.log("Secondary camera track published:", publication);
        if (publication.kind === "video") {
          const videoTrack = publication.track;
          if (videoTrack && "mediaStreamTrack" in videoTrack) {
            const mediaStreamTrack = (videoTrack as any).mediaStreamTrack;
            if (mediaStreamTrack && mediaStreamTrack.getSettings) {
              const settings = mediaStreamTrack.getSettings();
              console.log("Secondary camera published video track settings:", {
                width: settings.width,
                height: settings.height,
                frameRate: settings.frameRate,
                aspectRatio: settings.width / settings.height,
                trackName: publication.trackName
              });
            }
          }
        }
      });

      // Set up detailed stats logging every 5 seconds
      this.statsInterval = setInterval(() => {
        room?.getStats()?.then((stats) => {
          if (stats && stats.length > 0) {
            const localVideoStats = stats[0]?.localVideoTrackStats || [];
            const localAudioStats = stats[0]?.localAudioTrackStats || [];
            const currentTime = Date.now();

            // Process video stats
            localVideoStats.forEach((stat) => {
              const trackSid = stat.trackSid || "unknown";
              const dimensions = `${stat.dimensions?.width || "N/A"}x${
                stat.dimensions?.height || "N/A"
              }`;
              const fps = stat.frameRate || "N/A";
              const bytesSent = stat.bytesSent || 0;

              // Calculate bandwidth in kbps based on difference from previous reading
              let bandwidthKbps = "0.00";
              if (this.previousStats[trackSid]) {
                const bytesDiff = bytesSent - this.previousStats[trackSid].bytesSent;
                const timeDiffSeconds =
                  (currentTime - this.previousStats[trackSid].timestamp) / 1000;
                if (timeDiffSeconds > 0) {
                  bandwidthKbps = ((bytesDiff * 8) / (timeDiffSeconds * 1000)).toFixed(2);
                }
              }

              // Store current stats for next calculation
              this.previousStats[trackSid] = {
                bytesSent: bytesSent,
                timestamp: currentTime
              };

            });

            // Process audio stats if needed (secondary camera typically doesn't have audio)
            if (localAudioStats.length > 0) {
              const audioStat = localAudioStats[0];
              const bytesSent = audioStat.bytesSent || 0;
              const audioTrackSid = audioStat.trackSid || "unknown-audio";

              // Calculate audio bandwidth in kbps based on difference from previous reading
              let audioBandwidthKbps = "0.00";
              if (this.previousStats[audioTrackSid]) {
                const bytesDiff = bytesSent - this.previousStats[audioTrackSid].bytesSent;
                const timeDiffSeconds =
                  (currentTime - this.previousStats[audioTrackSid].timestamp) / 1000;
                if (timeDiffSeconds > 0) {
                  audioBandwidthKbps = ((bytesDiff * 8) / (timeDiffSeconds * 1000)).toFixed(2);
                }
              }

              // Store current audio stats for next calculation
              this.previousStats[audioTrackSid] = {
                bytesSent: bytesSent,
                timestamp: currentTime
              };

              console.log("[Secondary Camera Audio Stats]");
              console.log(`  - Bandwidth: ${audioBandwidthKbps} kbps`);
            }
          }
        });
      }, 5000);

      this.options.onConnected();
      this.twilioRoom = room;
      console.log("Secondary camera Twilio connected");
    } catch (e) {
      options.onConnectionFailure(e);
    }
  }

  previewElement(videoRef) {
    const localParticipant: LocalParticipant = this.twilioRoom.localParticipant;
    const localVideoTrack = Array.from(localParticipant.videoTracks.values())[0].track;
    if (videoRef.current && localVideoTrack) {
      this.videoTrack = localVideoTrack;
      this.videoTrack.attach(videoRef.current);
      this.videoTrack.on("stopped", this.handleTrackStopped);
    }
  }

  disconnect(): void {
    console.log("Secondary camera Twilio Disconnected");
    clearInterval(this.statsInterval);
    if (this.twilioRoom) {
      this.twilioRoom?.disconnect();
      if (this.videoTrack) {
        this.videoTrack?.stop();
      }
    }
  }

  private handleTrackStopped() {
    if (this.twilioRoom) {
      this.twilioRoom.disconnect();
      this.options.onTrackChange();
    }
  }
}
